import { useCallback } from 'react';
import { extractUrl, makeAbsoluteUrl } from '../utils/urlUtils';

export const useBannerManager = () => {
  // Create banner object for existing banners loaded from API
  const createExistingBanner = useCallback((bannerInput) => {
    if (!bannerInput) return null;
    
    const extractedUrl = extractUrl(bannerInput);
    const absoluteUrl = makeAbsoluteUrl(extractedUrl);
    
    if (!absoluteUrl) return null;
    
    return {
      url: absoluteUrl,
      isExisting: true,
      preview: absoluteUrl,
    };
  }, []);

  // Create banner object for new uploaded files
  const createNewBanner = useCallback((file, preview) => {
    if (!file) return null;
    
    return {
      file,
      preview,
      isExisting: false,
      isNew: true,
    };
  }, []);

  // Check if banner is a new file that should be uploaded
  const isNewBannerFile = useCallback((banner) => {
    return banner && banner.file && !banner.isExisting;
  }, []);

  // Check if banner is an existing one that should be preserved
  const isExistingBanner = useCallback((banner) => {
    return banner && banner.isExisting && !banner.file;
  }, []);

  // Get banner URL for display
  const getBannerUrl = useCallback((banner) => {
    if (!banner) return null;
    return banner.preview || banner.url;
  }, []);

  // Prepare banner for form submission
  const prepareBannerForSubmission = useCallback((banner, isFormData = false) => {
    if (!banner) return null;

    if (isFormData) {
      // For FormData submission, only return file if it's new
      return isNewBannerFile(banner) ? banner.file : null;
    } else {
      // For JSON submission
      if (isExistingBanner(banner)) {
        // Don't send existing banners in JSON
        return undefined;
      } else if (isNewBannerFile(banner)) {
        // This shouldn't happen in JSON mode, but handle gracefully
        return null;
      } else {
        // Remove banner
        return null;
      }
    }
  }, [isNewBannerFile, isExistingBanner]);

  return {
    createExistingBanner,
    createNewBanner,
    isNewBannerFile,
    isExistingBanner,
    getBannerUrl,
    prepareBannerForSubmission,
  };
};